import React, { useState, useCallback, useEffect } from 'react';
import {
  Modal,
  TextInput,
  Button,
  Stack,
  Group,
  Select,
  Switch,
  Skeleton,
  Text,
  Alert,
  Divider
} from '@mantine/core';
import { IconDeviceFloppy, IconX, IconBuildingStore, IconAlertCircle } from '@tabler/icons-react';
import { UpdateUserCommand } from '@/application/dto/UserDtos';
import {
  useUserQuery,
  useUpdateUserMutation,
  useAssignBusinessMutation,
  useRemoveBusinessMutation
} from '@/application/queries/userQueries';
import { useAuth } from '../../hooks/useAuth';
import { useSearchBusinessesQuery } from '@/application/queries/businessQueries';

export interface EditUserModalProps {
  isOpen: boolean;
  userId: string | null;
  onClose: () => void;
  onSuccess?: () => void;
}

/**
 * EditUserModal - Modal for editing an existing user (admin only)
 *
 * Features:
 * - Fetch-on-demand: Loads full user details when modal opens
 * - Edit profile fields (firstName, lastName, userName, email, displayName, phoneNumber)
 * - Business assignment/removal
 * - Status management (Active/Inactive)
 * - Form validation
 * - Success/error notifications via mutation hooks
 * - Loading state during fetch and save
 */
export const EditUserModal: React.FC<EditUserModalProps> = ({
  isOpen,
  userId,
  onClose,
  onSuccess
}): React.ReactElement => {
  const { user } = useAuth();
  const updateUserMutation = useUpdateUserMutation();
  const assignBusinessMutation = useAssignBusinessMutation();
  const removeBusinessMutation = useRemoveBusinessMutation();

  // Fetch user details when modal opens (fetch-on-demand)
  const {
    data: userDetail,
    isLoading: isLoadingUser,
    error: userError
  } = useUserQuery(
    userId ?? '',
    user?.role ?? 'Admin',
    user?.id ?? '',
    isOpen && !!userId // Only fetch when modal is open and userId is provided
  );

  // Form state
  const [formData, setFormData] = useState({
    userName: '',
    email: '',
    firstName: '',
    lastName: '',
    displayName: '',
    phoneNumber: '',
    isActive: true,
    isEmailNotificationsEnabled: true,
    isSmsNotificationsEnabled: false
  });

  // Business assignment state
  const [businessSearchQuery, setBusinessSearchQuery] = useState('');
  const [selectedBusinessId, setSelectedBusinessId] = useState<string>('');

  // Form validation errors
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Business search
  const { data: businessResults } = useSearchBusinessesQuery(
    businessSearchQuery,
    10,
    undefined,
    businessSearchQuery.length > 0
  );

  /**
   * Populate form when user data loads
   */
  useEffect(() => {
    if (userDetail && isOpen) {
      setFormData({
        userName: userDetail.userName,
        email: userDetail.email,
        firstName: userDetail.firstName ?? '',
        lastName: userDetail.lastName ?? '',
        displayName: userDetail.displayName ?? '',
        phoneNumber: userDetail.phoneNumber ?? '',
        isActive: userDetail.isActive,
        isEmailNotificationsEnabled: userDetail.isEmailNotificationsEnabled,
        isSmsNotificationsEnabled: userDetail.isSmsNotificationsEnabled
      });
      setSelectedBusinessId(userDetail.businessId ?? '');
      setErrors({});
    }
  }, [userDetail, isOpen]);

  /**
   * Handle form field changes
   */
  const handleChange = useCallback(
    (field: string, value: string | boolean): void => {
      setFormData((prev) => ({
        ...prev,
        [field]: value
      }));
      // Clear error for this field
      if (errors[field as string]) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[field as string];
          return newErrors;
        });
      }
    },
    [errors]
  );

  /**
   * Validate form
   */
  const validate = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};

    // Username validation
    if (formData.userName && formData.userName.length < 3) {
      newErrors['userName'] = 'Username must be at least 3 characters';
    }

    // Email validation
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors['email'] = 'Invalid email format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  /**
   * Handle form submission (update user profile)
   */
  const handleSubmit = useCallback(async (): Promise<void> => {
    if (!validate() || !userId) {
      return;
    }

    try {
      // Build UpdateUserCommand (only include changed fields)
      const command: UpdateUserCommand = {
        id: userId,
        userName: formData.userName || undefined,
        email: formData.email || undefined,
        firstName: formData.firstName || undefined,
        lastName: formData.lastName || undefined,
        displayName: formData.displayName || undefined,
        phoneNumber: formData.phoneNumber || undefined,
        isActive: formData.isActive,
        isEmailNotificationsEnabled: formData.isEmailNotificationsEnabled,
        isSmsNotificationsEnabled: formData.isSmsNotificationsEnabled
      };

      await updateUserMutation.mutateAsync({
        id: userId,
        command,
        userRole: user?.role ?? 'Admin'
      });

      // Close modal and notify parent
      onClose();
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      // Error is handled by mutation hook (notifications)
      console.error('Update user failed:', error);
    }
  }, [formData, validate, userId, updateUserMutation, user, onClose, onSuccess]);

  /**
   * Handle business assignment
   */
  const handleAssignBusiness = useCallback(async (): Promise<void> => {
    if (!userId || !selectedBusinessId) {
      return;
    }

    try {
      await assignBusinessMutation.mutateAsync({
        userId,
        businessId: selectedBusinessId,
        userRole: user?.role ?? 'Admin'
      });

      // Refetch user data to show updated business
      // React Query will auto-refetch due to mutation invalidation
    } catch (error) {
      console.error('Assign business failed:', error);
    }
  }, [userId, selectedBusinessId, assignBusinessMutation, user]);

  /**
   * Handle business removal
   */
  const handleRemoveBusiness = useCallback(async (): Promise<void> => {
    if (!userId) {
      return;
    }

    try {
      await removeBusinessMutation.mutateAsync({
        userId,
        userRole: user?.role ?? 'Admin'
      });

      setSelectedBusinessId('');
      // React Query will auto-refetch due to mutation invalidation
    } catch (error) {
      console.error('Remove business failed:', error);
    }
  }, [userId, removeBusinessMutation, user]);

  /**
   * Handle cancel
   */
  const handleCancel = useCallback((): void => {
    onClose();
  }, [onClose]);

  // Prepare business options for select
  const businessOptions =
    businessResults?.map((business) => ({
      value: business.id,
      label: business.name
    })) ?? [];

  // Loading state
  if (isLoadingUser && isOpen) {
    return (
      <Modal opened={isOpen} onClose={onClose} title="Edit User" size="lg" centered>
        <Stack gap="md">
          <Skeleton height={40} />
          <Skeleton height={40} />
          <Skeleton height={40} />
          <Skeleton height={40} />
          <Skeleton height={40} />
        </Stack>
      </Modal>
    );
  }

  // Error state
  if (userError && isOpen) {
    return (
      <Modal opened={isOpen} onClose={onClose} title="Edit User" size="lg" centered>
        <Alert icon={<IconAlertCircle />} title="Error Loading User" color="red">
          {userError.message}
        </Alert>
        <Group justify="flex-end" mt="md">
          <Button onClick={onClose}>Close</Button>
        </Group>
      </Modal>
    );
  }

  return (
    <Modal opened={isOpen} onClose={onClose} title="Edit User" size="lg" centered>
      <Stack gap="md">
        {/* User Profile Section */}
        <Text size="sm" fw={600} c="dimmed">
          User Profile
        </Text>

        {/* Username */}
        <TextInput
          label="Username"
          placeholder="Enter username"
          value={formData.userName}
          onChange={(e) => handleChange('userName', e.currentTarget.value)}
          error={errors['userName']}
        />

        {/* Email */}
        <TextInput
          label="Email"
          placeholder="user@example.com"
          type="email"
          value={formData.email}
          onChange={(e) => handleChange('email', e.currentTarget.value)}
          error={errors['email']}
        />

        {/* First Name & Last Name */}
        <Group grow>
          <TextInput
            label="First Name"
            placeholder="John"
            value={formData.firstName}
            onChange={(e) => handleChange('firstName', e.currentTarget.value)}
          />

          <TextInput
            label="Last Name"
            placeholder="Doe"
            value={formData.lastName}
            onChange={(e) => handleChange('lastName', e.currentTarget.value)}
          />
        </Group>

        {/* Display Name */}
        <TextInput
          label="Display Name"
          placeholder="Optional display name"
          value={formData.displayName}
          onChange={(e) => handleChange('displayName', e.currentTarget.value)}
        />

        {/* Phone Number */}
        <TextInput
          label="Phone Number"
          placeholder="(555) 123-4567"
          value={formData.phoneNumber}
          onChange={(e) => handleChange('phoneNumber', e.currentTarget.value)}
        />

        {/* Status */}
        <Switch
          label="Active"
          description="Inactive users cannot log in"
          checked={formData.isActive}
          onChange={(e) => handleChange('isActive', e.currentTarget.checked)}
        />

        <Divider my="sm" />

        {/* Notification Preferences Section */}
        <Text size="sm" fw={600} c="dimmed">
          Notification Preferences
        </Text>

        <Switch
          label="Email Notifications"
          description="Receive notifications via email"
          checked={formData.isEmailNotificationsEnabled}
          onChange={(e) => handleChange('isEmailNotificationsEnabled', e.currentTarget.checked)}
        />

        <Switch
          label="SMS Notifications"
          description="Receive notifications via SMS (requires phone number)"
          checked={formData.isSmsNotificationsEnabled}
          onChange={(e) => handleChange('isSmsNotificationsEnabled', e.currentTarget.checked)}
          disabled={!formData.phoneNumber}
        />

        <Divider my="sm" />

        {/* Business Assignment Section */}
        <Text size="sm" fw={600} c="dimmed">
          Business Assignment
        </Text>

        {userDetail?.businessName && (
          <Alert icon={<IconBuildingStore />} color="blue" variant="light">
            Currently assigned to: <strong>{userDetail.businessName}</strong>
          </Alert>
        )}

        <Select
          label="Business"
          placeholder="Search and select business"
          value={selectedBusinessId}
          onChange={(value) => setSelectedBusinessId(value ?? '')}
          data={businessOptions}
          searchable
          onSearchChange={setBusinessSearchQuery}
          nothingFoundMessage="No businesses found"
          leftSection={<IconBuildingStore size={16} />}
        />

        <Group>
          <Button
            size="sm"
            onClick={handleAssignBusiness}
            loading={assignBusinessMutation.isPending}
            disabled={!selectedBusinessId || assignBusinessMutation.isPending}
          >
            Assign Business
          </Button>

          {userDetail?.businessId && (
            <Button
              size="sm"
              variant="light"
              color="red"
              onClick={handleRemoveBusiness}
              loading={removeBusinessMutation.isPending}
              disabled={removeBusinessMutation.isPending}
            >
              Remove Business
            </Button>
          )}
        </Group>

        {/* Action Buttons */}
        <Group justify="flex-end" mt="md">
          <Button variant="subtle" leftSection={<IconX size={16} />} onClick={handleCancel}>
            Cancel
          </Button>

          <Button
            leftSection={<IconDeviceFloppy size={16} />}
            onClick={handleSubmit}
            loading={updateUserMutation.isPending}
            disabled={updateUserMutation.isPending}
          >
            Save Changes
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
